import React, { useEffect, useMemo, useState } from "react";
import {
  FaBalanceScale,
  FaBan,
  FaCheckCircle,
  FaCookieBite,
  FaEnvelope,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaFileContract,
  FaGlobe,
  FaInfoCircle,
  FaLock,
  FaShieldAlt,
  FaTools,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import { getSettings } from "../../services/settingsServices";
import Footer from "../Home/HomePage/Footer";
import ScrollIndicator from "../../components/ScrollIndicator/ScrollIndicator";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  siret_club: "",
  tel_club: "",
};

const getDisplayValue = (value, fallback = "Non renseigné") => {
  const clean = String(value || "").trim();
  return clean || fallback;
};

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const normalizeWebsite = (site = "") => {
  const clean = String(site || "").trim();

  if (!clean && typeof window !== "undefined") {
    return window.location.origin;
  }

  return clean || "";
};

const SectionCard = ({ id, icon: Icon, title, children }) => (
  <article
    id={id}
    className="scroll-mt-32 rounded-[2rem] border border-slate-200 bg-white/90 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85 sm:p-8"
  >
    <div className="mb-5 flex items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon />
      </div>

      <div>
        <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
          {title}
        </h2>
      </div>
    </div>

    <div className="space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
      {children}
    </div>
  </article>
);

const SummaryCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-[1.7rem] border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
    <Icon className="mb-4 text-2xl text-primary" />
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
      {label}
    </p>
    <p className="mt-2 break-words font-barlow text-xl font-bold text-slate-950 dark:text-white">
      {value}
    </p>
  </div>
);

const InfoList = ({ items = [] }) => (
  <ul className="grid gap-3">
    {items.map((item) => (
      <li
        key={item}
        className="flex gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold leading-6 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
      >
        <FaCheckCircle className="mt-1 shrink-0 text-primary" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function ConditionsUtilisationPage() {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
          siteweb_club:
            clubSettings.siteweb_club ||
            clubSettings.website ||
            clubSettings.site_url ||
            "",
          mail_club:
            clubSettings.mail_club ||
            clubSettings.email_club ||
            clubSettings.email ||
            "",
          tel_club:
            clubSettings.tel_club ||
            clubSettings.telephone_club ||
            clubSettings.phone ||
            "",
          adresse_club:
            clubSettings.adresse_club ||
            clubSettings.address ||
            [clubSettings.adresse, clubSettings.code_postal, clubSettings.ville]
              .filter(Boolean)
              .join(" "),
          responsable_club:
            clubSettings.responsable_club ||
            clubSettings.responsable_publication ||
            clubSettings.president_club ||
            "",
        });
      } catch (error) {
        console.error(
          "Erreur lors du chargement des conditions d’utilisation :",
          error
        );
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const siteUrl = useMemo(
    () => normalizeWebsite(settings.siteweb_club),
    [settings.siteweb_club]
  );

  const mailHref = useMemo(() => {
    return settings.mail_club ? `mailto:${settings.mail_club}` : "";
  }, [settings.mail_club]);

  const currentDate = new Date().toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  const navItems = [
    ["Objet", "objet"],
    ["Accès au site", "acces"],
    ["Acceptation", "acceptation"],
    ["Services proposés", "services"],
    ["Comptes utilisateurs", "comptes"],
    ["Obligations", "obligations"],
    ["Contenus", "contenus"],
    ["Responsabilité", "responsabilite"],
    ["Données personnelles", "donnees"],
    ["Liens externes", "liens"],
    ["Modification", "modification"],
    ["Droit applicable", "droit"],
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-16 pt-36 text-white sm:px-6 lg:px-8 lg:pb-20 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="relative mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                <FaFileContract className="text-primary" />
                Textes réglementaires
              </div>

              <h1 className="mt-6 font-barlow text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                Conditions
                <br />
                d’utilisation
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">
                Cette page définit les principales règles d’accès et
                d’utilisation du site web du club, de ses contenus publics, de
                ses formulaires et, le cas échéant, de son espace
                d’administration.
              </p>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-5 md:grid-cols-3">
              <SummaryCard
                icon={FaUsers}
                label="Éditeur"
                value={getDisplayValue(settings.nom_club, "Club / association")}
              />

              <SummaryCard
                icon={FaGlobe}
                label="Site internet"
                value={getDisplayValue(siteUrl)}
              />

              <SummaryCard
                icon={FaEnvelope}
                label="Contact"
                value={getDisplayValue(settings.mail_club)}
              />
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-[280px_1fr]">
              <aside className="hidden lg:block">
                <div className="sticky top-32 rounded-[2rem] border border-slate-200 bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85">
                  <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Sommaire
                  </p>

                  <nav className="flex flex-col gap-2">
                    {navItems.map(([label, id]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="rounded-2xl px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-orange-50 hover:text-primary dark:text-slate-300 dark:hover:bg-white/10"
                      >
                        {label}
                      </a>
                    ))}
                  </nav>

                  <div className="mt-5 rounded-2xl bg-orange-50 p-4 text-sm leading-6 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                    <div className="mb-2 flex items-center gap-2 font-bold">
                      <FaInfoCircle />
                      Mise à jour
                    </div>
                    Dernière mise à jour automatique : {currentDate}.
                  </div>
                </div>
              </aside>

              <div className="space-y-8">
                <SectionCard id="objet" icon={FaInfoCircle} title="1. Objet">
                  <p>
                    Les présentes conditions d’utilisation ont pour objet de
                    définir les modalités d’accès et d’utilisation du site web
                    édité par{" "}
                    <strong>
                      {getDisplayValue(settings.nom_club, "le club")}
                    </strong>
                    .
                  </p>

                  <p>
                    Le site permet notamment de présenter le club ou
                    l’association, ses activités, ses disciplines, ses horaires,
                    ses tarifs, ses actualités, ses albums photos, ses
                    partenaires, ainsi que les moyens de contact ou de demande
                    d’inscription lorsqu’ils sont activés.
                  </p>
                </SectionCard>

                <SectionCard
                  id="acces"
                  icon={FaGlobe}
                  title="2. Accès au site"
                >
                  <p>
                    Le site est accessible à tout utilisateur disposant d’un
                    accès à internet. Les frais liés à l’accès au site,
                    notamment les frais de matériel, de logiciel ou de connexion
                    internet, restent à la charge de l’utilisateur.
                  </p>

                  <p>
                    Certaines parties du site peuvent être réservées aux
                    utilisateurs autorisés, notamment l’espace d’administration
                    permettant au club de gérer les contenus et paramètres du
                    site.
                  </p>

                  <p>
                    L’éditeur du site peut suspendre, limiter ou interrompre
                    temporairement l’accès au site pour des raisons de
                    maintenance, de sécurité, de mise à jour ou de force majeure.
                  </p>
                </SectionCard>

                <SectionCard
                  id="acceptation"
                  icon={FaCheckCircle}
                  title="3. Acceptation des conditions"
                >
                  <p>
                    L’accès et l’utilisation du site impliquent l’acceptation des
                    présentes conditions d’utilisation. Si l’utilisateur ne
                    souhaite pas accepter ces conditions, il est invité à ne pas
                    utiliser le site.
                  </p>

                  <p>
                    L’utilisation d’un formulaire de contact, d’un formulaire
                    d’inscription ou d’un espace privé peut impliquer
                    l’acceptation de règles complémentaires précisées au moment
                    de l’utilisation du service concerné.
                  </p>
                </SectionCard>

                <SectionCard
                  id="services"
                  icon={FaTools}
                  title="4. Services proposés"
                >
                  <p>
                    Le site peut proposer différents services, selon les
                    fonctionnalités activées par le club :
                  </p>

                  <InfoList
                    items={[
                      "Consultation d’informations publiques sur le club, les disciplines, les horaires, les tarifs et les actualités.",
                      "Consultation d’albums photos ou de contenus de communication publiés par le club.",
                      "Formulaire de contact permettant d’envoyer un message au club.",
                      "Formulaire de demande d’inscription ou de préinscription.",
                      "Espace d’administration réservé aux personnes autorisées par le club.",
                    ]}
                  />

                  <p>
                    Les informations publiées sur le site sont fournies à titre
                    indicatif. Le club peut les modifier, les compléter ou les
                    supprimer à tout moment.
                  </p>
                </SectionCard>

                <SectionCard
                  id="comptes"
                  icon={FaUserShield}
                  title="5. Comptes utilisateurs et espace d’administration"
                >
                  <p>
                    L’accès à l’espace d’administration est réservé aux
                    utilisateurs disposant d’identifiants valides. Ces
                    identifiants sont strictement personnels et confidentiels.
                  </p>

                  <p>
                    L’utilisateur disposant d’un accès privé s’engage à ne pas
                    communiquer ses identifiants à un tiers, à informer le club
                    en cas de suspicion d’utilisation frauduleuse, et à utiliser
                    l’espace d’administration uniquement dans le cadre des
                    missions qui lui sont confiées.
                  </p>

                  <p>
                    Le club peut retirer, suspendre ou modifier les droits
                    d’accès d’un utilisateur lorsque cela est nécessaire pour la
                    sécurité, la bonne gestion du site ou le respect des règles
                    internes de l’association.
                  </p>
                </SectionCard>

                <SectionCard
                  id="obligations"
                  icon={FaBan}
                  title="6. Obligations de l’utilisateur"
                >
                  <p>
                    L’utilisateur s’engage à utiliser le site de manière loyale,
                    raisonnable et conforme à la loi. Il s’interdit notamment :
                  </p>

                  <InfoList
                    items={[
                      "D’utiliser le site à des fins illégales, frauduleuses, abusives ou contraires à l’ordre public.",
                      "De perturber ou tenter de perturber le fonctionnement du site, du serveur ou des services associés.",
                      "De tenter d’accéder sans autorisation à l’espace d’administration, aux données ou aux comptes d’autres utilisateurs.",
                      "De transmettre volontairement des virus, scripts malveillants, spams ou contenus nuisibles.",
                      "De publier ou transmettre des contenus injurieux, diffamatoires, discriminatoires, menaçants, obscènes ou portant atteinte aux droits d’autrui.",
                      "D’usurper l’identité d’une personne ou de communiquer volontairement des informations fausses.",
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  id="contenus"
                  icon={FaFileContract}
                  title="7. Contenus publiés et propriété intellectuelle"
                >
                  <p>
                    Les contenus disponibles sur le site, notamment les textes,
                    photographies, images, logos, documents, graphismes,
                    vidéos, éléments de mise en page et contenus éditoriaux,
                    sont protégés par les règles applicables en matière de
                    propriété intellectuelle.
                  </p>

                  <p>
                    Sauf autorisation préalable ou mention contraire, toute
                    reproduction, représentation, modification, adaptation,
                    diffusion ou exploitation de tout ou partie des contenus du
                    site est interdite.
                  </p>

                  <p>
                    Les contenus, marques, logos ou documents appartenant à des
                    tiers restent la propriété de leurs titulaires respectifs.
                    L’utilisateur s’engage à respecter les droits attachés à ces
                    contenus.
                  </p>
                </SectionCard>

                <SectionCard
                  id="responsabilite"
                  icon={FaExclamationTriangle}
                  title="8. Responsabilité"
                >
                  <p>
                    L’éditeur du site s’efforce de fournir des informations
                    fiables, exactes et régulièrement mises à jour. Toutefois,
                    des erreurs, omissions, retards de mise à jour ou
                    indisponibilités temporaires peuvent survenir.
                  </p>

                  <p>
                    L’éditeur ne pourra être tenu responsable des dommages
                    directs ou indirects résultant de l’utilisation du site, de
                    l’impossibilité d’y accéder, d’une erreur de contenu, d’une
                    interruption temporaire ou d’un dysfonctionnement technique.
                  </p>

                  <p>
                    L’utilisateur reste responsable de l’usage qu’il fait des
                    informations disponibles sur le site et des données qu’il
                    transmet volontairement via les formulaires.
                  </p>
                </SectionCard>

                <SectionCard
                  id="donnees"
                  icon={FaShieldAlt}
                  title="9. Données personnelles"
                >
                  <p>
                    Le site peut collecter des données personnelles lorsque
                    l’utilisateur utilise certains services, notamment les
                    formulaires de contact ou de demande d’inscription.
                  </p>

                  <p>
                    Les modalités de collecte, d’utilisation, de conservation et
                    d’exercice des droits des utilisateurs sont précisées dans la
                    politique de confidentialité du site.
                  </p>

                  <p>
                    Pour toute demande relative aux données personnelles,
                    l’utilisateur peut contacter le club à l’adresse suivante :{" "}
                    {settings.mail_club ? (
                      <a
                        href={mailHref}
                        className="font-bold text-primary hover:underline"
                      >
                        {settings.mail_club}
                      </a>
                    ) : (
                      <strong>email de contact non renseigné</strong>
                    )}
                    .
                  </p>
                </SectionCard>

                <SectionCard
                  id="liens"
                  icon={FaExternalLinkAlt}
                  title="10. Liens externes et services tiers"
                >
                  <p>
                    Le site peut contenir des liens vers des sites ou services
                    tiers, notamment des réseaux sociaux, partenaires,
                    fédérations, organismes publics ou plateformes externes.
                  </p>

                  <p>
                    Ces liens sont fournis à titre d’information. L’éditeur du
                    site ne contrôle pas les contenus, politiques de
                    confidentialité, pratiques ou disponibilités de ces sites
                    tiers et ne peut être tenu responsable de leur
                    fonctionnement.
                  </p>
                </SectionCard>

                <SectionCard
                  id="cookies"
                  icon={FaCookieBite}
                  title="11. Cookies et traceurs"
                >
                  <p>
                    Le site peut utiliser des cookies ou traceurs nécessaires à
                    son fonctionnement, à la sécurité, à la mesure de
                    fréquentation ou à l’amélioration de l’expérience
                    utilisateur lorsque ces fonctionnalités sont activées.
                  </p>

                  <p>
                    Certains cookies strictement nécessaires peuvent être utilisés
                    sans consentement préalable. Les cookies soumis à
                    consentement doivent faire l’objet d’une information et d’un
                    choix adapté lorsque ces outils sont utilisés.
                  </p>
                </SectionCard>

                <SectionCard
                  id="modification"
                  icon={FaTools}
                  title="12. Modification des conditions"
                >
                  <p>
                    Les présentes conditions d’utilisation peuvent être modifiées
                    à tout moment afin de tenir compte de l’évolution du site,
                    des services proposés, de l’organisation du club ou de la
                    réglementation applicable.
                  </p>

                  <p>
                    La version applicable est celle publiée sur le site au moment
                    de la consultation. L’utilisateur est invité à consulter
                    régulièrement cette page.
                  </p>
                </SectionCard>

                <SectionCard
                  id="droit"
                  icon={FaBalanceScale}
                  title="13. Droit applicable"
                >
                  <p>
                    Les présentes conditions d’utilisation sont soumises au droit
                    français.
                  </p>

                  <p>
                    En cas de difficulté, l’utilisateur est invité à contacter le
                    club afin de rechercher une solution amiable avant toute
                    autre démarche.
                  </p>

                  <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5 text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    <div className="mb-2 flex items-center gap-2 font-bold">
                      <FaInfoCircle />
                      Contact
                    </div>

                    <p>
                      Pour toute question relative aux présentes conditions,
                      vous pouvez contacter le club à l’adresse suivante :{" "}
                      {settings.mail_club ? (
                        <a
                          href={mailHref}
                          className="font-bold underline"
                        >
                          {settings.mail_club}
                        </a>
                      ) : (
                        <strong>email de contact non renseigné</strong>
                      )}
                      .
                    </p>
                  </div>
                </SectionCard>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}